import { api, endpoints } from '../../api';
import { uiText } from '../../i18n';

/* Все виды заказов моста — один список: проверка снимка, тип вида и обе очистки знаков (смена владельца, чужой владелец по свежему me).
   Новый вид добавляется только сюда, иначе его знак переживёт смену владельца. */
const KINDS = ['circle', 'lipsync', 'photo', 'video', 'dance', 'swap', 'text', 'collage', 'voice', 'avatar', 'reels', 'mcard', 'post', 'infog', 'shoot', 'pshoot', 'cover'] as const;
export interface StudioOrderSnapshot {
  owner: number;
  orderId: number;
  appId: number;
  status: 'queued' | 'running' | 'done' | 'failed' | 'uncertain';
  kind: (typeof KINDS)[number];
  shown: boolean;
}
const snapshotEvent = 'steos:studio:order';
const jobEvent = 'steos:studio:job';
const storageKey = (owner: number) => `steos:studio:jobs:${owner}`;
function valid(value: unknown): value is StudioOrderSnapshot {
  const v = value as StudioOrderSnapshot | null;
  return (
    !!v &&
    [v.owner, v.orderId, v.appId].every(id => Number.isSafeInteger(id) && id > 0) &&
    ['queued', 'running', 'done', 'failed', 'uncertain'].includes(v.status) &&
    (KINDS as readonly string[]).includes(v.kind) &&
    typeof v.shown === 'boolean'
  );
}
function ownerInShell() {
  const owner = (window as unknown as { BotServerMe?: { user?: { id?: number } } }).BotServerMe?.user?.id;
  return Number.isSafeInteger(owner) && Number(owner) > 0 ? Number(owner) : 0;
}
export function publishStudioOrder(snapshot: StudioOrderSnapshot) {
  if (valid(snapshot)) document.dispatchEvent(new CustomEvent(snapshotEvent, { detail: snapshot }));
}
interface Dependencies {
  owner(): number;
  me(signal: AbortSignal): Promise<{ user: { id: number } }>;
  get(id: number, signal: AbortSignal): Promise<{ status: string }>;
}
/** Сервис хранит только навигационную квитанцию. Ни восстановление, ни знак заказа не создают заказ повторно. */
export function createStudioJobBridge(deps: Dependencies) {
  let owner = 0,
    stopped = false,
    timer: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | null = null;
  const jobs = new Map<number, StudioOrderSnapshot>();
  const emit = (job: StudioOrderSnapshot) =>
    document.dispatchEvent(
      new CustomEvent(jobEvent, {
        detail: {
          ...job,
          native: true,
          name: uiText(
            job.kind === 'cover' ? 'Кавер' : job.kind === 'shoot' ? 'Фотосессия из селфи' : job.kind === 'pshoot' ? 'Фотосессия товара' : job.kind === 'reels' ? 'Рилс' : job.kind === 'mcard' ? 'Карточка товара' : job.kind === 'post' ? 'Карусель для поста' : job.kind === 'infog' ? 'Слайды с преимуществами' : job.kind === 'avatar'
              ? 'Свой аватар'
              : job.kind === 'voice'
                ? 'Запись'
                : job.kind === 'collage'
                  ? 'Коллаж из фото'
                  : job.kind === 'text'
                    ? 'Текст'
                    : job.kind === 'dance'
                      ? 'Повтори движения'
                      : job.kind === 'swap'
                        ? 'Стать персонажем'
                        : job.kind === 'photo'
                          ? 'Фото'
                          : job.kind === 'circle'
                            ? 'Кружок'
                            : 'Видео',
          ),
          st: job.status === 'done' ? 'done' : job.status === 'failed' ? 'fail' : 'run',
          phase: uiText(job.status === 'uncertain' ? 'Уточняем результат' : 'Создаётся'),
          done: uiText(
            job.kind === 'cover' ? 'Кавер готов' : job.kind === 'shoot' || job.kind === 'pshoot' ? 'Фото готовы' : job.kind === 'reels' ? 'Рилс готов' : job.kind === 'mcard' ? 'Карточка готова' : job.kind === 'post' || job.kind === 'infog' ? 'Слайды готовы' : job.kind === 'avatar'
              ? 'Вариант готов'
              : job.kind === 'voice'
                ? 'Запись готова'
                : job.kind === 'collage'
                  ? 'Коллаж готов'
                  : job.kind === 'text'
                    ? 'Текст готов'
                    : job.kind === 'photo'
                      ? 'Фото готовы'
                      : job.kind === 'circle'
                        ? 'Кружок готов'
                        : 'Видео готово',
          ),
          fail: uiText(
            job.kind === 'cover' ? 'Кавер не создан' : job.kind === 'shoot' || job.kind === 'pshoot' ? 'Фотосессия не создана' : job.kind === 'reels' ? 'Рилс не создан' : job.kind === 'mcard' ? 'Карточка не создана' : job.kind === 'post' || job.kind === 'infog' ? 'Слайды не созданы' : job.kind === 'avatar'
              ? 'Вариант не создан'
              : job.kind === 'voice'
                ? 'Запись не обработана'
                : job.kind === 'collage'
                  ? 'Коллаж не создан'
                  : job.kind === 'text'
                    ? 'Текст не создан'
                    : job.kind === 'photo'
                      ? 'Фото не создано'
                      : job.kind === 'circle'
                        ? 'Кружок не создан'
                        : 'Видео не создано',
          ),
        },
      }),
    );
  const clearBadge = (kind: StudioOrderSnapshot['kind']) =>
    document.dispatchEvent(new CustomEvent(jobEvent, { detail: { kind, clear: true, native: true } }));
  const clearBadges = () => {
    for (const kind of KINDS) clearBadge(kind);
  };
  const persist = () => {
    try {
      sessionStorage.setItem(storageKey(owner), JSON.stringify([...jobs.values()].slice(-8)));
    } catch {
      /* Недоступное хранилище не останавливает проверку уже известного заказа в текущей странице. */
    }
  };
  const syncOwner = () => {
    const next = deps.owner();
    if (next === owner) return;
    request?.abort();
    clearBadges();
    jobs.clear();
    owner = next;
    if (!owner) return;
    try {
      const raw = sessionStorage.getItem(storageKey(owner));
      if (raw && raw.length < 32000) {
        const values: unknown = JSON.parse(raw);
        if (Array.isArray(values))
          for (const value of values.slice(-8)) {
            if (valid(value) && value.owner === owner) jobs.set(value.orderId, { ...value, shown: false });
          }
      }
    } catch {
      /* Повреждённая навигационная квитанция не даёт права отправлять команды. */
    }
    for (const job of jobs.values()) emit(job);
  };
  const schedule = (delay = 5000) => {
    clearTimeout(timer);
    if (!stopped) timer = setTimeout(() => void poll(), delay);
  };
  const poll = async () => {
    syncOwner();
    const pending = [...jobs.values()].filter(job => !['done', 'failed'].includes(job.status));
    if (!owner || !pending.length) {
      schedule();
      return;
    }
    const expectedOwner = owner;
    const current = new AbortController();
    request = current;
    const timeout = setTimeout(() => current.abort(), 20000);
    try {
      const me = await deps.me(current.signal);
      if (stopped || current.signal.aborted || deps.owner() !== expectedOwner) return;
      if (me.user.id !== expectedOwner) {
        jobs.clear();
        persist();
        clearBadges();
        return;
      }
      await Promise.all(
        pending.map(async original => {
          const receipt = await deps.get(original.orderId, current.signal);
          if (stopped || current.signal.aborted || deps.owner() !== expectedOwner || !jobs.has(original.orderId)) return;
          const latest = jobs.get(original.orderId)!;
          if (['done', 'failed'].includes(latest.status) && !['done', 'failed'].includes(receipt.status)) return;
          const next = { ...latest, status: receipt.status };
          if (valid(next)) {
            jobs.set(next.orderId, next);
            emit(next);
          }
        }),
      );
      persist();
    } catch {
      current.abort();
      persist();
      /* Ошибка GET не является ни отказом заказа, ни разрешением на новый POST. */
    } finally {
      clearTimeout(timeout);
      if (request === current) request = null;
      schedule();
    }
  };
  const receive = (event: Event) => {
    syncOwner();
    const value: unknown = (event as CustomEvent).detail;
    if (!valid(value) || !owner || value.owner !== owner) return;
    if (value.shown && location.hash.split('.')[0] === '#studio') {
      // Точный серверный appId переживает reload. В URL нет текста, файлов или ключа финансовой команды.
      history.replaceState(null, '', `#studio.${value.kind}.native_app_${value.appId}`);
    }
    const previous = jobs.get(value.orderId);
    /* Итог уже показан на экране заказа, человек ушёл с него: знак «… не создан» или «… готово» больше не нужен — иначе он
       возвращается над меню после каждого выхода, хотя ошибку прочитали, а результат посмотрели (стенд 29.10). Результат — в «Истории» */
    if (previous?.shown && previous.status === value.status && !value.shown && (value.status === 'failed' || value.status === 'done')) {
      jobs.delete(value.orderId);
      persist();
      const remaining = [...jobs.values()].filter(item => item.kind === value.kind).at(-1);
      if (remaining) emit(remaining);
      else clearBadge(value.kind);
      return;
    }
    const next = {
      ...value,
      status:
        previous && ['done', 'failed'].includes(previous.status) && !['done', 'failed'].includes(value.status)
          ? previous.status
          : value.status,
    };
    jobs.delete(value.orderId);
    jobs.set(value.orderId, next);
    if (jobs.size > 8) jobs.delete(jobs.keys().next().value!);
    persist();
    emit(next);
    /* Тот же заказ пришёл с другим видом (родная форма «Фото» → экран движка «Фотосессии», Q-S8): знак прежнего вида переходит
       к последнему другому его заказу или снимается — иначе над меню два знака одного заказа */
    if (previous && previous.kind !== next.kind) {
      const remaining = [...jobs.values()].filter(item => item.kind === previous.kind).at(-1);
      if (remaining) emit(remaining);
      else clearBadge(previous.kind);
    }
  };
  const clear = (event: Event) => {
    syncOwner();
    const id = (event as CustomEvent).detail?.id;
    const job = jobs.get(id);
    if (!job || job.owner !== owner) return;
    jobs.delete(id);
    persist();
    const remaining = [...jobs.values()].filter(item => item.kind === job.kind).at(-1);
    if (remaining) emit(remaining);
    else clearBadge(job.kind);
  };
  const sync = () => {
    syncOwner();
    for (const job of jobs.values()) emit(job);
  };
  document.addEventListener(snapshotEvent, receive);
  document.addEventListener('steos:studio:order-clear', clear);
  document.addEventListener('steos:studio:job-sync', sync);
  syncOwner();
  schedule(0);
  return () => {
    stopped = true;
    clearTimeout(timer);
    request?.abort();
    document.removeEventListener(snapshotEvent, receive);
    document.removeEventListener('steos:studio:order-clear', clear);
    document.removeEventListener('steos:studio:job-sync', sync);
  };
}
let dispose: (() => void) | undefined;
export function installStudioJobBridge() {
  if (!dispose)
    dispose = createStudioJobBridge({
      owner: ownerInShell,
      me: signal => api.request<{ user: { id: number } }>('/app/me', { signal }),
      get: endpoints.speech.get,
    });
}
